import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { AppContext } from 'Context/AppContext';
import {
  flattenSectionsToValues,
  getResolveConfig,
  syncResolveCardReference,
  updateResolveConfig,
  ResolveConfigForbiddenError,
} from '../services/ResolveConfigService';
import { ManifestSection, ManifestSetting, ResolveConfigOverrides } from '../manifest/types';
import { effectiveValueOf, validateValue } from '../manifest/valueUtils';

type LoadState = 'loading' | 'empty' | 'error' | 'forbidden' | 'success';

interface ResolveConfigContextValue {
  /** `GMSResolve:update` — whether this caller may PUT changes at all. Reaching this
   * screen only implies `GMSResolve:read`, so a viewer without update must not see a
   * Save control that would otherwise fail server-side with a permission error. */
  canUpdate: boolean;
  loadState: LoadState;
  /** The full schema+value tree from the last successful GET — what the generic renderer
   * loops over. Metadata only; current in-progress edits live in `overrides`. */
  sections: ManifestSection[];
  /** Every top-level setting, across every section, keyed by name — how
   * `dependsOnSatisfied` resolves a `depends_on.setting` reference. */
  settingsByName: Map<string, ManifestSetting>;
  overrides: ResolveConfigOverrides;
  setOverride: (name: string, value: unknown) => void;
  /** What blocks Save, by setting name: every setting whose current value breaks a
   * `validateValue` rule the backend PUT also enforces, whether it was stored that way or
   * edited that way. Derived from the values, so it can never go stale. */
  errors: Record<string, string | null>;
  channelKey: string | undefined;
  /** The page-level Save's dirty flag — every setting EXCEPT `product_card_id`, which
   * has its own Save in the Product card dialog (see `isProductCardDirty`). */
  isDirty: boolean;
  saving: boolean;
  /** Page-level Save: PUTs only the settings changed since the last load or save, never
   * `product_card_id`. */
  save: () => Promise<void>;
  /** The Product card dialog's dirty flag — `product_card_id` alone. */
  isProductCardDirty: boolean;
  /** Product card dialog Save: PUTs `product_card_id` alone. */
  saveProductCard: () => Promise<void>;
  /** Discards an unsaved Product card pick (the dialog was closed without saving). */
  resetProductCard: () => void;
  reload: () => void;
}

const ResolveConfigContext = createContext<ResolveConfigContextValue | null>(null);

export const useResolveConfig = (): ResolveConfigContextValue => {
  const ctx = useContext(ResolveConfigContext);
  if (!ctx) throw new Error('useResolveConfig must be used within ResolveConfigProvider');
  return ctx;
};

/** `product_card_id` is saved only from the Product card dialog, never by the page Save. */
const PRODUCT_CARD_KEY = 'product_card_id';

/** The saved card id, or `null` — an absent key, `null` and a non-string all mean "no card". */
const cardIdOf = (values: ResolveConfigOverrides): string | null =>
  typeof values[PRODUCT_CARD_KEY] === 'string' ? (values[PRODUCT_CARD_KEY] as string) : null;

const withoutProductCard = (values: ResolveConfigOverrides): ResolveConfigOverrides => {
  const rest = { ...values };
  delete rest[PRODUCT_CARD_KEY];
  return rest;
};

const buildSettingsByName = (sections: ManifestSection[]): Map<string, ManifestSetting> => {
  const map = new Map<string, ManifestSetting>();
  for (const section of sections) {
    for (const setting of section.settings ?? []) map.set(setting.name, setting);
  }
  return map;
};

export const ResolveConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const appData = useContext(AppContext) as any;
  const channelKey: string | undefined = appData?.channelKey;
  const canUpdate: boolean = Boolean(appData?.pm?.checkPermission('gmsresolve_update'));

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [sections, setSections] = useState<ManifestSection[]>([]);
  const [savedOverrides, setSavedOverrides] = useState<ResolveConfigOverrides>({});
  const [overrides, setOverrides] = useState<ResolveConfigOverrides>({});
  const [saving, setSaving] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!channelKey) {
      setLoadState('empty');
      return undefined;
    }
    setLoadState('loading');
    getResolveConfig(channelKey)
      .then((loadedSections) => {
        if (cancelled) return;
        const loadedOverrides = flattenSectionsToValues(loadedSections);
        setSections(loadedSections);
        setSavedOverrides(loadedOverrides);
        setOverrides(loadedOverrides);
        setLoadState(loadedSections.length ? 'success' : 'empty');
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadState(err instanceof ResolveConfigForbiddenError ? 'forbidden' : 'error');
      });
    return () => {
      cancelled = true;
    };
    // Switching Environment must load THAT environment's values, never leak the last one.
  }, [channelKey, reloadCounter]);

  const settingsByName = useMemo(() => buildSettingsByName(sections), [sections]);

  const setOverride = useCallback((name: string, value: unknown) => {
    setOverrides((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Derived, not stored: a value the backend rejects may never have been edited (a chips list
  // stored over its bounds, a stored non-hex colour, a `required` setting left at its empty
  // default). Deriving every `validateValue` rule flags the row from first render, re-checks
  // it on every change, and can never outlive the values it describes — or leak into another
  // Environment. A stored error blocks Save even though only changed settings are sent: the
  // merchant should fix it while it is on screen.
  const errors = useMemo(() => {
    const found: Record<string, string | null> = {};
    settingsByName.forEach((setting, name) => {
      // `product_card_id` is not a manifest setting: its options come from the card list
      // (`options.source`), it has its own dialog and Save, and the page Save never sends it.
      if (name === PRODUCT_CARD_KEY) return;
      found[name] = validateValue(setting, effectiveValueOf(setting, overrides));
    });
    return found;
  }, [settingsByName, overrides]);

  const isDirty = useMemo(
    () => JSON.stringify(withoutProductCard(overrides)) !== JSON.stringify(withoutProductCard(savedOverrides)),
    [overrides, savedOverrides],
  );

  const isProductCardDirty = cardIdOf(overrides) !== cardIdOf(savedOverrides);

  const save = useCallback(async () => {
    if (!channelKey || !canUpdate) return;
    const blockingError = Object.values(errors).some(Boolean);
    if (blockingError) {
      toast.error('Fix the highlighted fields before saving.');
      return;
    }
    // The backend PUT merges per setting name, so the body carries only what changed:
    // an untouched setting stays "never set" in storage and keeps following its manifest
    // default, and a save from another tab or user to a different setting is not
    // overwritten with this page's stale copy. Leaving `product_card_id` out leaves the
    // stored card untouched — an unsaved pick in the dialog is not saved by this button.
    const edited = withoutProductCard(overrides);
    const body = Object.fromEntries(
      Object.entries(edited).filter(([name, value]) => JSON.stringify(value) !== JSON.stringify(savedOverrides[name])),
    );
    setSaving(true);
    try {
      const ok = await updateResolveConfig(channelKey, body);
      if (!ok) throw new Error('save failed');
      setSavedOverrides((prev) => ({ ...prev, ...body }));
      toast.success('Resolve configuration saved.');
    } catch {
      // A failed save leaves previously stored values as they were.
      toast.error('Could not save the configuration. Nothing was changed.');
    } finally {
      setSaving(false);
    }
  }, [channelKey, overrides, savedOverrides, errors, canUpdate]);

  const saveProductCard = useCallback(async () => {
    if (!channelKey || !canUpdate) return;
    const previousCardId = cardIdOf(savedOverrides);
    const nextCardId = cardIdOf(overrides);
    setSaving(true);
    try {
      const ok = await updateResolveConfig(channelKey, { [PRODUCT_CARD_KEY]: nextCardId });
      if (!ok) throw new Error('save failed');
      setSavedOverrides((prev) => ({ ...prev, [PRODUCT_CARD_KEY]: nextCardId }));
      toast.success('Product card saved.');
      // Best-effort — see `syncResolveCardReference`'s docblock. Only runs once the
      // config write itself has landed, and only when the card selection moved.
      if (previousCardId !== nextCardId) {
        await syncResolveCardReference(channelKey, previousCardId, nextCardId);
      }
    } catch {
      toast.error('Could not save the product card. Nothing was changed.');
    } finally {
      setSaving(false);
    }
  }, [channelKey, overrides, canUpdate, savedOverrides]);

  const resetProductCard = useCallback(() => {
    setOverrides((prev) => ({ ...prev, [PRODUCT_CARD_KEY]: savedOverrides[PRODUCT_CARD_KEY] ?? null }));
  }, [savedOverrides]);

  const reload = useCallback(() => setReloadCounter((n) => n + 1), []);

  const value: ResolveConfigContextValue = {
    canUpdate,
    loadState,
    sections,
    settingsByName,
    overrides,
    setOverride,
    errors,
    channelKey,
    isDirty,
    saving,
    save,
    isProductCardDirty,
    saveProductCard,
    resetProductCard,
    reload,
  };

  return <ResolveConfigContext.Provider value={value}>{children}</ResolveConfigContext.Provider>;
};
